import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { School } from '../../types/admin';
import { School as SchoolIcon, Plus, ArrowRight } from 'lucide-react';
import { useSchool } from '../../contexts/SchoolContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

interface SchoolSelectorProps {
  onClose: () => void;
}

export default function SchoolSelector({ onClose }: SchoolSelectorProps) {
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [schoolCode, setSchoolCode] = useState('');
  const [newSchool, setNewSchool] = useState({
    name: '',
    type: 'public' as const,
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    website: '',
    gradeRange: {
      start: 'K',
      end: '12'
    }
  });

  const { selectedSchool, setSelectedSchool } = useSchool();
  const navigate = useNavigate();

  useEffect(() => {
    const loadSchools = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        // Get schools where user is admin or member
        const membershipSnapshot = await getDocs(
          query(
            collection(db, 'school_members'),
            where('userId', '==', user.uid)
          )
        );

        const schoolData: School[] = [];
        for (const memberDoc of membershipSnapshot.docs) {
          const schoolDoc = await getDoc(doc(db, 'schools', memberDoc.data().schoolId));
          if (schoolDoc.exists()) {
            schoolData.push({ id: schoolDoc.id, ...schoolDoc.data() } as School);
          }
        }

        setSchools(schoolData);
        setLoading(false);
      } catch (error) {
        console.error('Error loading schools:', error);
        toast.error('Failed to load schools');
        setLoading(false);
      }
    };

    loadSchools();
  }, []);

  const handleJoinSchool = async () => {
    if (!schoolCode.trim()) {
      toast.error('Please enter a school code');
      return;
    }

    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;

      // Verify school code
      const schoolQuery = query(
        collection(db, 'schools'),
        where('code', '==', schoolCode.trim())
      );

      const querySnapshot = await getDocs(schoolQuery);
      if (querySnapshot.empty) {
        toast.error('Invalid school code');
        return;
      }

      const schoolDoc = querySnapshot.docs[0];
      const school = { id: schoolDoc.id, ...schoolDoc.data() } as School;

      // Create school membership document
      const membershipId = `${school.id}_${user.uid}`;
      await setDoc(doc(db, 'school_members', membershipId), {
        userId: user.uid,
        schoolId: school.id,
        role: 'staff',
        joinedAt: new Date().toISOString()
      });

      await setSelectedSchool(school);
      toast.success('Successfully joined school');
      onClose();
      navigate('/dashboard');
    } catch (error) {
      console.error('Error joining school:', error);
      toast.error('Failed to join school');
    } finally {
      setLoading(false);
    }
  };

  const generateSchoolCode = (name: string) => {
    const prefix = name.substring(0, 3).toUpperCase();
    const year = new Date().getFullYear();
    const random = Math.random().toString(36).substring(2, 5).toUpperCase();
    return `${prefix}${year}${random}`;
  };

  const handleCreateSchool = async () => {
    if (!newSchool.name.trim()) {
      toast.error('Please enter a school name');
      return;
    }

    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;

      // Create a new document reference with auto-generated ID
      const schoolRef = doc(collection(db, 'schools'));
      const code = generateSchoolCode(newSchool.name);

      // Create the school object with the generated ID
      const schoolData: School = {
        id: schoolRef.id,
        name: newSchool.name.trim(),
        code,
        adminId: user.uid,
        type: newSchool.type,
        address: newSchool.address.trim(),
        city: newSchool.city.trim(),
        state: newSchool.state.trim(),
        zipCode: newSchool.zipCode.trim(),
        phone: newSchool.phone.trim(),
        website: newSchool.website.trim(),
        gradeRange: newSchool.gradeRange,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        settings: {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          academicYear: new Date().getFullYear().toString(),
          terms: [],
          departments: []
        },
        stats: {
          totalStaff: 1,
          totalStudents: 0,
          activeUsers: 1
        }
      };

      // Save the school document
      await setDoc(schoolRef, schoolData);

      // Create school membership for the admin
      const membershipId = `${schoolRef.id}_${user.uid}`;
      await setDoc(doc(db, 'school_members', membershipId), {
        userId: user.uid,
        schoolId: schoolRef.id,
        role: 'admin',
        joinedAt: new Date().toISOString()
      });

      // Update selected school in context
      await setSelectedSchool(schoolData);
      toast.success('School created successfully');
      onClose();
      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating school:', error);
      toast.error('Failed to create school');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSchool = async (school: School) => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;

      const membershipQuery = query(
        collection(db, 'school_members'),
        where('userId', '==', user.uid),
        where('schoolId', '==', school.id)
      );
      const membershipSnapshot = await getDocs(membershipQuery);

      if (membershipSnapshot.empty) {
        throw new Error('No valid membership found');
      }

      await setSelectedSchool(school);
      toast.success(`Selected ${school.name}`);
      onClose();
      navigate('/dashboard');
    } catch (error) {
      console.error('Error selecting school:', error);
      toast.error('Failed to select school');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="text-text-secondary">Loading schools...</div>
      </div>
    );
  }

  if (showCreateForm) {
    return (
      <div className="p-4">
        <h2 className="text-lg font-semibold text-text-primary mb-4">Create New School</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              School Name
            </label>
            <input
              type="text"
              value={newSchool.name}
              onChange={(e) => setNewSchool({ ...newSchool, name: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                       focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
              placeholder="e.g., Springfield High School"
              required
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateSchool}
              disabled={loading}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                       transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Create School
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="text-lg font-semibold text-text-primary mb-4">Select School</h2>

      {/* Join with Code */}
      <div className="mb-6">
        <div className="flex gap-2">
          <input
            type="text"
            value={schoolCode}
            onChange={(e) => setSchoolCode(e.target.value)}
            placeholder="Enter school code"
            className="flex-1 px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                     focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
          />
          <button
            onClick={handleJoinSchool}
            disabled={loading}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                     transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            Join
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Create New School */}
      <button
        onClick={() => setShowCreateForm(true)}
        className="w-full p-4 bg-surface-dark rounded-lg hover:bg-primary-50 transition-colors 
                 flex items-center gap-3 mb-4"
      >
        <div className="p-2 bg-primary-100 rounded-lg">
          <Plus className="w-5 h-5 text-primary-600" />
        </div>
        <span className="font-medium text-text-primary">Create New School</span>
      </button>

      {/* School List */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-text-secondary mb-2">Your Schools</h3>
        {schools.map((school) => (
          <button
            key={school.id}
            onClick={() => handleSelectSchool(school)}
            className={`w-full p-4 rounded-lg transition-colors flex items-center gap-3
                     ${selectedSchool?.id === school.id 
                       ? 'bg-primary-50 border-2 border-primary-500' 
                       : 'hover:bg-primary-50'}`}
          >
            <div className="p-2 bg-primary-100 rounded-lg">
              <SchoolIcon className="w-5 h-5 text-primary-600" />
            </div>
            <div className="flex-1 text-left">
              <h4 className="font-medium text-text-primary">{school.name}</h4>
              <p className="text-sm text-text-secondary">Code: {school.code}</p>
            </div>
          </button>
        ))}

        {schools.length === 0 && (
          <p className="text-center text-text-secondary py-4">
            No schools found. Join a school or create a new one.
          </p>
        )}
      </div>
    </div>
  );
}